import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingWidget from "@/components/BookingWidget";

interface PageData {
  title: string;
  meta_title: string | null;
  meta_description: string | null;
  meta_keywords: string | null;
  hero_image_url: string | null;
  content: string;
}

export default function LandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    supabase
      .from("landing_pages")
      .select("title, meta_title, meta_description, meta_keywords, hero_image_url, content")
      .eq("slug", slug)
      .eq("is_published", true)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) {
          navigate("/404", { replace: true });
          return;
        }
        setPage(data);
        setLoading(false);
      });
  }, [slug, navigate]);

  // SEO meta tags
  useEffect(() => {
    if (!page) return;
    document.title = page.meta_title || `${page.title} | World Transfers Trips`;

    const setMeta = (name: string, content: string | null) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", page.meta_description);
    setMeta("keywords", page.meta_keywords);
  }, [page]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-28 pb-16 flex items-center justify-center">
          <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!page) return null;

  const renderContent = (raw: string) => {
    const blocks = raw.split(/\n{2,}/);
    return blocks.map((block, i) => {
      const trimmed = block.trim();
      if (trimmed.startsWith("## ")) {
        return <h2 key={i} className="text-2xl font-display font-bold text-foreground mt-8 mb-4">{trimmed.slice(3)}</h2>;
      }
      if (trimmed.startsWith("### ")) {
        return <h3 key={i} className="text-xl font-display font-semibold text-foreground mt-6 mb-3">{trimmed.slice(4)}</h3>;
      }
      return <p key={i} className="text-muted-foreground leading-relaxed mb-4">{trimmed}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero with booking widget */}
      <section
        className="relative pt-24 pb-16 min-h-[500px] flex items-center"
        style={{
          backgroundImage: page.hero_image_url ? `url(${page.hero_image_url})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-tropical-dark/80" />
        <div className="absolute inset-0 bg-pattern-diagonal opacity-15" />
        <div className="relative container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-hero-foreground mb-4">
                {page.title}
              </h1>
              {page.meta_description && (
                <p className="text-hero-foreground/70 text-lg">{page.meta_description}</p>
              )}
            </div>
            <div>
              <BookingWidget />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          {renderContent(page.content)}
        </div>
      </article>

      <Footer />
    </div>
  );
}
